import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import ProfileImage from '../components/ProfileImage';
import { authHeaders } from '../utils/authService';
import { SessionTypeIcon } from '../utils/RaceImageMapper';
import LiveTelemetryPanel from '../components/LiveTelemetryPanel';
import LapDataBrowser from '../components/LapDataBrowser';
import IpAddressMap from '../components/IpAddressMap';
import EmailPanel from '../components/EmailPanel';
import LoadingProgress from '../components/LoadingProgress';
import TrackThumbnail from '../components/TrackThumbnail';

const ADMIN_EMAIL = 'stevespano009@gmail.com';

// Requests that gate the first paint of the dashboard, in the order shown while loading
const ADMIN_LOAD_PARTS = ['users', 'tracks', 'app versions', 'cars'];

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--color-bg)',
  },
  content: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '32px 24px',
  },
  title: {
    fontFamily: 'var(--font-heading)',
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
    marginBottom: 24,
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16,
    marginBottom: 16,
  },
  activityStatsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
    marginBottom: 32,
  },
  statCard: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: '20px',
    textAlign: 'center',
  },
  statValue: {
    fontFamily: 'var(--font-data)',
    fontSize: 32,
    fontWeight: 700,
    color: 'var(--color-accent)',
  },
  statLabel: {
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    color: 'var(--color-text-dim)',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginTop: 6,
  },
  statMeta: {
    marginTop: 10,
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: 'var(--color-text)',
    lineHeight: 1.45,
  },
  statMetaMono: {
    marginTop: 4,
    fontFamily: 'var(--font-data)',
    fontSize: 11,
    color: 'var(--color-text-dim)',
  },
  tabs: {
    display: 'flex',
    gap: 8,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  tab: {
    padding: '8px 20px',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    letterSpacing: 1.5,
    cursor: 'pointer',
    textTransform: 'uppercase',
  },
  tabActive: {
    background: 'var(--color-primary)',
    borderColor: 'var(--color-primary)',
    color: '#fff',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    overflow: 'hidden',
  },
  th: {
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--color-text-dim)',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    padding: '14px 16px',
    textAlign: 'left',
    borderBottom: '1px solid var(--color-border)',
    background: 'var(--color-surface-light)',
  },
  sortHeaderBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    font: 'inherit',
    letterSpacing: 'inherit',
    textTransform: 'inherit',
    cursor: 'pointer',
  },
  td: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--color-text)',
    padding: '12px 16px',
    borderBottom: '1px solid var(--color-border)',
  },
  tdMono: {
    fontFamily: 'var(--font-data)',
    fontSize: 12,
    color: 'var(--color-text)',
    padding: '12px 16px',
    borderBottom: '1px solid var(--color-border)',
  },
  badge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
  },
  badgeAdmin: { background: 'rgba(231, 76, 60, 0.15)', color: '#e74c3c' },
  badgeUser: { background: 'rgba(46, 134, 193, 0.15)', color: '#2e86c1' },
  badgeVerified: { background: 'rgba(39, 174, 96, 0.15)', color: '#27ae60' },
  badgeUnverified: { background: 'rgba(243, 156, 18, 0.15)', color: '#f39c12' },
  badgeGithub: { background: 'rgba(155, 89, 182, 0.15)', color: '#9b59b6' },
  badgeCustom: { background: 'rgba(46, 134, 193, 0.15)', color: '#2e86c1' },
  badgeSessionType: { background: 'rgba(13, 110, 253, 0.14)', color: '#0d6efd' },
  searchRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1,
    maxWidth: 400,
    padding: '10px 14px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
  },
  filterSelect: {
    minWidth: 220,
    padding: '10px 14px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
  },
  filterResetBtn: {
    padding: '10px 16px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    letterSpacing: 1.3,
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  deleteBtn: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid var(--color-danger)',
    borderRadius: 4,
    color: 'var(--color-danger)',
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    cursor: 'pointer',
  },
  syncBtn: {
    padding: '8px 20px',
    background: 'var(--color-primary)',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.5,
    cursor: 'pointer',
    textTransform: 'uppercase',
  },
  loading: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text-dim)',
    textAlign: 'center',
    padding: 40,
  },
  emptyRow: {
    textAlign: 'center',
    color: 'var(--color-text-dim)',
    padding: '30px 16px',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
  },
  expandBtn: {
    padding: '4px 10px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
    color: 'var(--color-accent)',
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    cursor: 'pointer',
  },
  detailPanel: {
    background: 'var(--color-surface-light)',
    padding: '16px 24px',
    borderBottom: '1px solid var(--color-border)',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 12,
  },
  detailLabel: {
    fontFamily: 'var(--font-heading)',
    fontSize: 9,
    color: 'var(--color-text-dim)',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  detailValue: {
    fontFamily: 'var(--font-data)',
    fontSize: 13,
    color: 'var(--color-text)',
  },
  // User management form styles
  formOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  formCard: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 12,
    padding: '32px',
    width: 420,
    maxWidth: '90vw',
  },
  formTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 20,
  },
  formGroup: {
    marginBottom: 16,
  },
  formLabel: {
    display: 'block',
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    color: 'var(--color-text-dim)',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  formInput: {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  formSelect: {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  formActions: {
    display: 'flex',
    gap: 12,
    marginTop: 20,
  },
  formSubmit: {
    flex: 1,
    padding: '10px',
    background: 'var(--color-primary)',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    fontFamily: 'var(--font-heading)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.5,
    cursor: 'pointer',
    textTransform: 'uppercase',
  },
  formCancel: {
    flex: 1,
    padding: '10px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-heading)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.5,
    cursor: 'pointer',
    textTransform: 'uppercase',
  },
  formError: {
    color: 'var(--color-danger)',
    fontSize: 13,
    fontFamily: 'var(--font-body)',
    marginTop: 12,
  },
  formSuccess: {
    color: 'var(--color-success, #27ae60)',
    fontSize: 13,
    fontFamily: 'var(--font-body)',
    marginTop: 12,
  },
  actionBtn: {
    padding: '4px 10px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
    color: 'var(--color-accent)',
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    cursor: 'pointer',
  },
};

function isUserAdmin(u) {
  return u.email === ADMIN_EMAIL || u.role === 'admin';
}

function getAuthHeaders() {
  return authHeaders();
}


function formatTimestamp(ts) {
  if (!ts) return 'Never';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getSessionTimestampValue(session) {
  if (!session) return 0;

  if (session.createdAt) {
    const createdAt = new Date(session.createdAt).getTime();
    if (!Number.isNaN(createdAt)) return createdAt;
  }

  if (session.sessionId) {
    const match = String(session.sessionId).match(/(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})$/);
    if (match) {
      const parsed = Date.parse(`${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}.000Z`);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }

  if (session.date) {
    const parsed = Date.parse(`${session.date}T00:00:00.000Z`);
    if (!Number.isNaN(parsed)) return parsed;
  }

  return 0;
}

function formatSessionTimestamp(session) {
  const tsValue = getSessionTimestampValue(session);
  if (!tsValue) return session?.date || '-';

  return new Date(tsValue).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

function formatLapTime(seconds) {
  if (!seconds || seconds <= 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds - mins * 60;
  const wholeS = Math.floor(secs);
  const ms = Math.round((secs - wholeS) * 1000);
  return `${mins}:${String(wholeS).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}

function timeAgo(ts) {
  if (!ts) return 'Never';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function getActivityLastSeen(entry) {
  const candidates = [entry?.lastUpload, entry?.lastLogin].filter(Boolean);
  if (candidates.length === 0) return null;
  return candidates.sort((a, b) => new Date(b) - new Date(a))[0];
}

function getLatestActivityUser(users, appNames) {
  return users
    .filter((u) => String(u.email || '').toLowerCase() !== ADMIN_EMAIL.toLowerCase())
    .map((u) => {
      const lastSeen = appNames
        .map((appName) => getActivityLastSeen(u.appActivity?.[appName]))
        .filter(Boolean)
        .sort((a, b) => new Date(b) - new Date(a))[0] || null;
      return { user: u, lastSeen };
    })
    .filter((entry) => entry.lastSeen)
    .sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen))[0] || null;
}

// ── Add User Modal ──
function AddUserModal({ onClose, onUserCreated }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/admin/users', {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, password, displayName, role }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to create user');
        return;
      }
      onUserCreated(data);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.formOverlay} onClick={onClose}>
      <form style={styles.formCard} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <div style={styles.formTitle}>Add New User</div>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Email</label>
          <input style={styles.formInput} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Display Name</label>
          <input style={styles.formInput} type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Password</label>
          <input style={styles.formInput} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Role</label>
          <select style={styles.formSelect} value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="user">User</option>
            <option value="admin">Administrator</option>
          </select>
        </div>
        {error && <div style={styles.formError}>{error}</div>}
        <div style={styles.formActions}>
          <button type="button" style={styles.formCancel} onClick={onClose}>Cancel</button>
          <button type="submit" style={{ ...styles.formSubmit, opacity: loading ? 0.6 : 1 }} disabled={loading}>
            {loading ? 'Creating...' : 'Create User'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Change Password Modal ──
function ChangePasswordModal({ targetUser, onClose }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/admin/users/${targetUser.id}/password`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to change password');
        return;
      }
      setSuccess('Password updated successfully');
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.formOverlay} onClick={onClose}>
      <form style={styles.formCard} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <div style={styles.formTitle}>Change Password</div>
        <div style={{ ...styles.syncStatus, padding: '0 0 16px 0' }}>
          For: <strong>{targetUser.email}</strong>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>New Password</label>
          <input style={styles.formInput} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" autoFocus />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Confirm Password</label>
          <input style={styles.formInput} type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" />
        </div>
        {error && <div style={styles.formError}>{error}</div>}
        {success && <div style={styles.formSuccess}>{success}</div>}
        <div style={styles.formActions}>
          <button type="button" style={styles.formCancel} onClick={onClose}>Cancel</button>
          <button type="submit" style={{ ...styles.formSubmit, opacity: loading ? 0.6 : 1 }} disabled={loading}>
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminDashboard({ user, onLogout, onViewSessions, onViewTrack, onSelectSession, onProfile, onEditProfile }) {
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [cars, setCars] = useState([]);
  const [carsVersion, setCarsVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [loadedParts, setLoadedParts] = useState([]);
  const [appVersions, setAppVersions] = useState({});
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [selectedUserEmail, setSelectedUserEmail] = useState('all');
  const [selectedUserTrack, setSelectedUserTrack] = useState('all');
  const [selectedUserDate, setSelectedUserDate] = useState('all');
  const [selectedSessionType, setSelectedSessionType] = useState('all');
  const [sessionSortOrder, setSessionSortOrder] = useState('desc');

  const [showDebug, setShowDebug] = useState(false);
  const [thumbStatus, setThumbStatus] = useState('');
  const [thumbBusy, setThumbBusy] = useState(false);

  // User management modals
  const [showAddUser, setShowAddUser] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setSessionsLoading(true);
    setLoadedParts([]);
    const markLoaded = (part, request) => request.finally(() => setLoadedParts((prev) => prev.concat(part)));

    // The all-sessions scan is the slow call (full-file read per session on a
    // cold cache). Load it independently so it doesn't gate the rest of the
    // page — users/tracks/versions paint immediately and the Sessions tab shows
    // its own spinner until this resolves.
    fetch('/api/admin/sessions', { headers: getAuthHeaders() })
      .then((r) => r.json())
      .then((sessionsRes) => setSessions(Array.isArray(sessionsRes) ? sessionsRes : []))
      .catch((err) => console.error('Error loading sessions:', err))
      .finally(() => setSessionsLoading(false));

    try {
      const [usersRes, tracksRes, versionsRes, carsRes] = await Promise.all([
        markLoaded('users', fetch('/api/admin/users', { headers: getAuthHeaders() }).then((r) => r.json()).catch(() => [])),
        markLoaded('tracks', fetch(`/api/tracks${showDebug ? '?debug=1' : ''}`, { headers: getAuthHeaders() }).then((r) => r.json()).catch(() => [])),
        markLoaded('app versions', fetch('/api/admin/app-versions', { headers: getAuthHeaders() }).then((r) => r.json()).catch(() => ({}))),
        markLoaded('cars', fetch('/api/admin/cars', { headers: getAuthHeaders() }).then((r) => r.json()).catch(() => ({}))),
      ]);
      const userList = usersRes.map((u) => ({ id: u.userId, ...u }));
      setUsers(userList);
      setTracks(tracksRes);
      setAppVersions(versionsRes);
      setCars(Array.isArray(carsRes.cars) ? carsRes.cars : []);
      setCarsVersion(carsRes.version || '');
    } catch (err) {
      console.error('Error loading admin data:', err);
    } finally {
      setLoading(false);
    }
  }, [showDebug]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteUser = async (userId, userEmail) => {
    if (userEmail === ADMIN_EMAIL) return;
    if (!window.confirm(`Delete user ${userEmail}?\n\nThis will permanently remove their account and profile data.`)) return;

    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers,
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Failed to delete user');
        return;
      }
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Failed to delete user: ' + err.message);
    }
  };

  const handleToggleRole = async (userId, userEmail, currentRole) => {
    if (userEmail === ADMIN_EMAIL) return;
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const action = newRole === 'admin' ? 'promote to Administrator' : 'demote to User';
    if (!window.confirm(`${action} for ${userEmail}?`)) return;

    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ role: newRole }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Failed to update role');
        return;
      }
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, role: newRole } : u));
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Failed to update role: ' + err.message);
    }
  };

  const handleToggleVerified = async (userId, currentVerified) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/admin/users/${userId}/verified`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ emailVerified: !currentVerified }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Failed to update verified status');
        return;
      }
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, emailVerified: !currentVerified } : u));
    } catch (err) {
      console.error('Error updating verified status:', err);
      alert('Failed to update verified status: ' + err.message);
    }
  };

  const handleUserCreated = () => {
    loadData();
  };

  const handleGenerateMissingThumbnails = async () => {
    setThumbBusy(true);
    setThumbStatus('Generating missing thumbnails...');
    try {
      const res = await fetch('/api/tracks/thumbnails/all?missing=true', { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      setThumbStatus(`Generated ${data.generated} thumbnails${data.skipped ? `, ${data.skipped} skipped` : ''}${data.failed ? `, ${data.failed} failed` : ''}`);
      loadData();
    } catch (err) {
      setThumbStatus('Error: ' + err.message);
    } finally {
      setThumbBusy(false);
    }
  };

  const handleRegenerateAllThumbnails = async () => {
    if (!window.confirm('Regenerate all track thumbnails? This will overwrite existing SVGs.')) return;
    setThumbBusy(true);
    setThumbStatus('Regenerating all thumbnails...');
    try {
      const res = await fetch('/api/tracks/thumbnails/all', { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      setThumbStatus(`Regenerated ${data.generated} thumbnails${data.failed ? `, ${data.failed} failed` : ''}`);
      loadData();
    } catch (err) {
      setThumbStatus('Error: ' + err.message);
    } finally {
      setThumbBusy(false);
    }
  };

  const handleDeleteAllThumbnails = async () => {
    if (!window.confirm('Delete all track thumbnails?')) return;
    setThumbBusy(true);
    setThumbStatus('Deleting all thumbnails...');
    try {
      const res = await fetch('/api/tracks/thumbnails/all', { method: 'DELETE', headers: getAuthHeaders() });
      const data = await res.json();
      setThumbStatus(`Deleted ${data.deleted} thumbnails`);
      loadData();
    } catch (err) {
      setThumbStatus('Error: ' + err.message);
    } finally {
      setThumbBusy(false);
    }
  };

  const handleClearTrackCache = async () => {
    setThumbBusy(true);
    setThumbStatus('Clearing track cache...');
    try {
      const res = await fetch('/api/tracks/cache/clear', { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      setThumbStatus(`Track cache cleared — ${data.trackCount} tracks reloaded`);
      loadData();
    } catch (err) {
      setThumbStatus('Error: ' + err.message);
    } finally {
      setThumbBusy(false);
    }
  };

  const handleGenerateOne = async (trackId) => {
    setThumbBusy(true);
    try {
      await fetch(`/api/tracks/${trackId}/thumbnail`, { method: 'POST', headers: getAuthHeaders() });
      loadData();
    } catch (err) {
      setThumbStatus('Error: ' + err.message);
    } finally {
      setThumbBusy(false);
    }
  };

  const handleDeleteOne = async (trackId) => {
    setThumbBusy(true);
    try {
      await fetch(`/api/tracks/${trackId}/thumbnail`, { method: 'DELETE', headers: getAuthHeaders() });
      loadData();
    } catch (err) {
      setThumbStatus('Error: ' + err.message);
    } finally {
      setThumbBusy(false);
    }
  };

  const userEmailOptions = users
    .map((u) => u.email)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const sessionsForSelectedEmail = selectedUserEmail === 'all'
    ? sessions
    : sessions.filter((s) => s.userEmail === selectedUserEmail);

  const userTrackOptions = Array.from(
    new Set(sessionsForSelectedEmail.map((s) => s.track).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const sessionsForSelectedTrack = selectedUserTrack === 'all'
    ? sessionsForSelectedEmail
    : sessionsForSelectedEmail.filter((s) => s.track === selectedUserTrack);

  const userDateOptions = Array.from(
    new Set(sessionsForSelectedTrack.map((s) => s.date).filter(Boolean))
  ).sort((a, b) => b.localeCompare(a));

  const sessionsForSelectedDate = selectedUserDate === 'all'
    ? sessionsForSelectedTrack
    : sessionsForSelectedTrack.filter((s) => s.date === selectedUserDate);

  const sessionTypeOptions = Array.from(
    new Set(sessionsForSelectedDate.map((s) => s.sessionType).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const filteredUsers = users.filter(
    (u) =>
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sessionFilteredResults = sessions.filter((s) =>
    (selectedUserEmail === 'all' || s.userEmail === selectedUserEmail) &&
    (selectedUserTrack === 'all' || s.track === selectedUserTrack) &&
    (selectedUserDate === 'all' || s.date === selectedUserDate) &&
    (selectedSessionType === 'all' || s.sessionType === selectedSessionType)
  );

  const filteredSessions = sessionFilteredResults
    .filter(
      (s) =>
        s.track?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.userId?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const diff = getSessionTimestampValue(a) - getSessionTimestampValue(b);
      if (diff !== 0) return sessionSortOrder === 'desc' ? -diff : diff;

      const sessionIdCompare = String(b.sessionId || '').localeCompare(String(a.sessionId || ''));
      if (sessionIdCompare !== 0) return sessionSortOrder === 'desc' ? sessionIdCompare : -sessionIdCompare;

      return String(a.track || '').localeCompare(String(b.track || ''));
    });

  const filteredTracks = tracks.filter(
    (t) => t.trackName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const carSearch = searchTerm.trim().toLowerCase();
  const filteredCars = carSearch
    ? cars.filter((c) => `${c.Make} ${c.Model} ${c.Year}`.toLowerCase().includes(carSearch))
    : cars;
  const carMakeCount = new Set(cars.map((c) => c.Make)).size;

  const getUserSessionCount = (userId) => sessions.filter((s) => s.userId === userId).length;

  const latestCloudActivity = getLatestActivityUser(users, ['RaceVoiceCloud']);
  const latestSimActivity = getLatestActivityUser(users, ['RaceVoiceSim']);
  const latestAppDesktopActivity = getLatestActivityUser(users, ['RaceVoiceApp', 'RaceVoiceDesktop']);
  const recentLogins = users
    .filter((u) => u.appActivity?.RaceVoiceCloud?.lastLogin)
    .sort((a, b) => new Date(b.appActivity?.RaceVoiceCloud?.lastLogin) - new Date(a.appActivity?.RaceVoiceCloud?.lastLogin));

  const latestActivityName =
    latestCloudActivity?.user?.displayName ||
    latestCloudActivity?.user?.email ||
    'N/A';
  const latestActivityTime = latestCloudActivity?.lastSeen || null;
  const latestSimActivityName =
    latestSimActivity?.user?.displayName ||
    latestSimActivity?.user?.email ||
    'N/A';
  const latestSimActivityTime = latestSimActivity?.lastSeen || null;
  const latestAppDesktopActivityName =
    latestAppDesktopActivity?.user?.displayName ||
    latestAppDesktopActivity?.user?.email ||
    'N/A';
  const latestAppDesktopActivityTime = latestAppDesktopActivity?.lastSeen || null;

  const totalSessions = sessions.length;
  const verifiedUsers = users.filter((u) => u.emailVerified).length;
  const githubTracks = tracks.filter((t) => t.source === 'github').length;
  const customTracks = tracks.filter((t) => t.source === 'user').length;
  const trackTypeCounts = tracks.reduce((acc, t) => {
    const type = t.metadata?.trackType || 'unknown';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  useEffect(() => {
    if (selectedUserEmail !== 'all' && !userEmailOptions.includes(selectedUserEmail)) {
      setSelectedUserEmail('all');
    }
  }, [selectedUserEmail, userEmailOptions]);

  useEffect(() => {
    if (selectedUserTrack !== 'all' && !userTrackOptions.includes(selectedUserTrack)) {
      setSelectedUserTrack('all');
    }
  }, [selectedUserTrack, userTrackOptions]);

  useEffect(() => {
    if (selectedUserDate !== 'all' && !userDateOptions.includes(selectedUserDate)) {
      setSelectedUserDate('all');
    }
  }, [selectedUserDate, userDateOptions]);

  useEffect(() => {
    if (selectedSessionType !== 'all' && !sessionTypeOptions.includes(selectedSessionType)) {
      setSelectedSessionType('all');
    }
  }, [selectedSessionType, sessionTypeOptions]);

  const resetSessionFilters = () => {
    setSelectedUserEmail('all');
    setSelectedUserTrack('all');
    setSelectedUserDate('all');
    setSelectedSessionType('all');
  };

  return (
    <div style={styles.page} data-doc-id="admin-dashboard">
      <Header user={user} onLogout={onLogout} onDashboard={onViewSessions} onProfile={onProfile} />
      <div style={styles.content}>
        <div style={styles.title}>ADMIN DASHBOARD</div>

        {/* Stats Overview */}
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{users.length}</div>
            <div style={styles.statLabel}>Total Users</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{verifiedUsers}</div>
            <div style={styles.statLabel}>Verified</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{totalSessions}</div>
            <div style={styles.statLabel}>Track Sessions</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{tracks.length}</div>
            <div style={styles.statLabel}>Tracks</div>
          </div>
        </div>
        <div style={styles.activityStatsRow}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{latestActivityTime ? timeAgo(latestActivityTime) : 'N/A'}</div>
            <div style={styles.statLabel}>Last Activity</div>
            <div style={styles.statMeta}>{latestActivityName}</div>
            <div style={styles.statMetaMono}>{latestActivityTime ? formatTimestamp(latestActivityTime) : 'No recent activity'}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{latestSimActivityTime ? timeAgo(latestSimActivityTime) : 'N/A'}</div>
            <div style={styles.statLabel}>Last RaceVoiceSim</div>
            <div style={styles.statMeta}>{latestSimActivityName}</div>
            <div style={styles.statMetaMono}>{latestSimActivityTime ? formatTimestamp(latestSimActivityTime) : 'No recent activity'}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{latestAppDesktopActivityTime ? timeAgo(latestAppDesktopActivityTime) : 'N/A'}</div>
            <div style={styles.statLabel}>Last App/Desktop</div>
            <div style={styles.statMeta}>{latestAppDesktopActivityName}</div>
            <div style={styles.statMetaMono}>{latestAppDesktopActivityTime ? formatTimestamp(latestAppDesktopActivityTime) : 'No recent activity'}</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabs} data-doc-id="admin-tabs">
          {['users', 'sessions', 'tracks', 'cars', 'activity', 'live', 'lapdata', 'maps', 'email'].map((tab) => (
            <button
              key={tab}
              data-doc-id={`admin-tab-${tab}`}
              style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}
              onClick={() => { setActiveTab(tab); setSearchTerm(''); }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Search + actions */}
        <div style={styles.searchRow}>
          <input
            data-doc-id="admin-search"
            style={styles.searchInput}
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            style={{ ...styles.tab, fontSize: 11, padding: '8px 16px' }}
            onClick={loadData}
            data-doc-id="admin-refresh"
          >
            Refresh
          </button>
          {activeTab === 'users' && (
            <button
              style={styles.syncBtn}
              onClick={() => setShowAddUser(true)}
              data-doc-id="admin-add-user"
            >
              Add User
            </button>
          )}
        </div>

        {activeTab === 'sessions' && (
          <div style={styles.searchRow} data-doc-id="admin-session-filters">
            <select
              data-doc-id="admin-filter-user"
              style={styles.filterSelect}
              value={selectedUserEmail}
              onChange={(e) => setSelectedUserEmail(e.target.value)}
            >
              <option value="all">All Users</option>
              {userEmailOptions.map((email) => (
                <option key={email} value={email}>
                  {email}
                </option>
              ))}
            </select>
            <select
              data-doc-id="admin-filter-track"
              style={styles.filterSelect}
              value={selectedUserTrack}
              onChange={(e) => setSelectedUserTrack(e.target.value)}
            >
              <option value="all">All Tracks</option>
              {userTrackOptions.map((track) => (
                <option key={track} value={track}>
                  {track}
                </option>
              ))}
            </select>
            <select
              data-doc-id="admin-filter-day"
              style={styles.filterSelect}
              value={selectedUserDate}
              onChange={(e) => setSelectedUserDate(e.target.value)}
            >
              <option value="all">All Days</option>
              {userDateOptions.map((date) => (
                <option key={date} value={date}>
                  {date}
                </option>
              ))}
            </select>
            <select
              data-doc-id="admin-filter-session-type"
              style={styles.filterSelect}
              value={selectedSessionType}
              onChange={(e) => setSelectedSessionType(e.target.value)}
            >
              <option value="all">All Session Types</option>
              {sessionTypeOptions.map((sessionType) => (
                <option key={sessionType} value={sessionType}>
                  {sessionType}
                </option>
              ))}
            </select>
            <button style={styles.filterResetBtn} onClick={resetSessionFilters} data-doc-id="admin-filter-reset">
              Reset Filters
            </button>
          </div>
        )}

        {loading ? (
          <LoadingProgress
            label="Loading data"
            done={loadedParts.length}
            total={ADMIN_LOAD_PARTS.length}
            detail={`Waiting for ${ADMIN_LOAD_PARTS.filter((p) => !loadedParts.includes(p)).join(', ')}`}
            expectedMs={6000}
          />
        ) : (
          <>
            {/* Users Tab */}
            {activeTab === 'users' && (
              <table style={styles.table} data-doc-id="admin-users-table">
                <thead>
                  <tr>
                    <th style={styles.th}>User</th>
                    <th style={styles.th}>Email / User ID</th>
                    <th style={styles.th}>Role</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Joined</th>
                    <th style={styles.th}>Last Login</th>
                    <th style={styles.th}>RaceVoiceSim</th>
                    <th style={styles.th}>RaceVoiceApp</th>
                    <th style={styles.th}>RaceVoiceDesktop</th>
                    <th style={styles.th}>Total Sessions</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr><td colSpan={11} style={styles.emptyRow}>No users found</td></tr>
                  ) : (
                    filteredUsers.map((u) => (
                      <React.Fragment key={u.id}>
                        <tr data-doc-id="admin-user-row">
                          <td style={styles.td}>
                            <div
                              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                              title={`View sessions for ${u.displayName || u.email}`}
                              onClick={() => { setSelectedUserEmail(u.email); setActiveTab('sessions'); }}
                            >
                              <ProfileImage uid={u.id} profileImage={u.profileImage} size={28} />
                              <span style={{ color: 'var(--color-accent)', textDecoration: 'underline' }}>{u.displayName || '-'}</span>
                            </div>
                          </td>
                          <td style={styles.tdMono}>
                            <div>{u.email}</div>
                            <div style={{ color: 'var(--color-text-dim)', fontSize: 10 }}>{u.id || '-'}</div>
                          </td>
                          <td style={styles.td}>
                            <span style={{
                              ...styles.badge,
                              ...(isUserAdmin(u) ? styles.badgeAdmin : styles.badgeUser),
                            }}>
                              {isUserAdmin(u) ? 'Admin' : 'User'}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <span
                              style={{
                                ...styles.badge,
                                ...(u.emailVerified ? styles.badgeVerified : styles.badgeUnverified),
                                cursor: 'pointer',
                              }}
                              title={`Click to ${u.emailVerified ? 'unverify' : 'verify'} user`}
                              onClick={() => handleToggleVerified(u.id, u.emailVerified)}
                            >
                              {u.emailVerified ? 'Verified' : 'Unverified'}
                            </span>
                          </td>
                          <td style={styles.tdMono}>{formatTimestamp(u.createdAt)}</td>
                          <td style={styles.tdMono}>
                            {(() => {
                              const act = u.appActivity?.RaceVoiceCloud;
                              if (!act?.lastLogin) return 'Never';
                              return (<>
                                <div>{formatTimestamp(act.lastLogin)}</div>
                                <div style={{ color: 'var(--color-text-dim)', fontSize: 10 }}>
                                  Logins={act.logins || 0}
                                </div>
                              </>);
                            })()}
                          </td>
                          {(() => {
                            const act = u.appActivity?.RaceVoiceSim;
                            const currentVersion = appVersions.RaceVoiceSim?.windows?.version;
                            const isOutdated = act?.lastVersion && currentVersion && act.lastVersion !== currentVersion;
                            return (
                              <td style={{ ...styles.tdMono, ...(isOutdated ? { backgroundColor: 'rgba(255, 255, 150, 0.15)' } : {}) }}>
                                {(!act || (!act.lastUpload && !act.lastLogin)) ? 'Never' : (<>
                                  <div>{formatTimestamp(act.lastUpload || act.lastLogin)}</div>
                                  {act.lastVersion && <div style={{ color: 'var(--color-accent)', fontSize: 10 }}>v{act.lastVersion}</div>}
                                  <div style={{ color: 'var(--color-text-dim)', fontSize: 10 }}>
                                    Logins={act.logins || 0} Uploads={act.uploads || 0}
                                  </div>
                                </>)}
                              </td>
                            );
                          })()}
                          {(() => {
                            const act = u.appActivity?.RaceVoiceApp;
                            const iosVer = appVersions.RaceVoiceApp?.ios;
                            const currentVersion = iosVer ? `${iosVer.version}+${iosVer.build}` : null;
                            const isOutdated = act?.lastVersion && currentVersion && act.lastVersion !== currentVersion;
                            return (
                              <td style={{ ...styles.tdMono, ...(isOutdated ? { backgroundColor: 'rgba(255, 255, 150, 0.15)' } : {}) }}>
                                {(!act || (!act.lastUpload && !act.lastLogin)) ? 'Never' : (<>
                                  <div>{formatTimestamp(act.lastUpload || act.lastLogin)}</div>
                                  {act.lastVersion && <div style={{ color: 'var(--color-accent)', fontSize: 10 }}>v{act.lastVersion}</div>}
                                  <div style={{ color: 'var(--color-text-dim)', fontSize: 10 }}>
                                    Logins={act.logins || 0} Uploads={act.uploads || 0}
                                  </div>
                                </>)}
                              </td>
                            );
                          })()}
                          <td style={styles.tdMono}>
                            {(() => {
                              const act = u.appActivity?.RaceVoiceDesktop;
                              if (!act || (!act.lastUpload && !act.lastLogin)) return 'Never';
                              const lastTime = act.lastUpload || act.lastLogin;
                              return (<>
                                {lastTime && <div>{formatTimestamp(lastTime)}</div>}
                                {act.lastVersion && <div style={{ color: 'var(--color-accent)', fontSize: 10 }}>v{act.lastVersion}</div>}
                                <div style={{ color: 'var(--color-text-dim)', fontSize: 10 }}>
                                  Logins={act.logins || 0} Uploads={act.uploads || 0}
                                </div>
                              </>);
                            })()}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>{getUserSessionCount(u.id)}</td>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button
                                style={styles.expandBtn}
                                onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
                              >
                                {expandedUser === u.id ? 'Hide' : 'Details'}
                              </button>
                              <button
                                style={styles.actionBtn}
                                onClick={() => onEditProfile({ uid: u.id, email: u.email, displayName: u.displayName })}
                              >
                                Profile
                              </button>
                              <button
                                style={styles.actionBtn}
                                onClick={() => setPasswordTarget(u)}
                              >
                                Password
                              </button>
                              {u.email !== ADMIN_EMAIL && (
                                <button
                                  style={styles.actionBtn}
                                  onClick={() => handleToggleRole(u.id, u.email, u.role)}
                                >
                                  {isUserAdmin(u) ? 'Demote' : 'Promote'}
                                </button>
                              )}
                              {u.email !== ADMIN_EMAIL && (
                                <button
                                  style={styles.deleteBtn}
                                  onClick={() => handleDeleteUser(u.id, u.email)}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expandedUser === u.id && (
                          <tr>
                            <td colSpan={10} style={styles.detailPanel}>
                              <div style={styles.detailGrid}>
                                <div>
                                  <div style={styles.detailLabel}>User ID</div>
                                  <div style={styles.detailValue}>{u.id}</div>
                                </div>
                                <div>
                                  <div style={styles.detailLabel}>Display Name</div>
                                  <div style={styles.detailValue}>{u.displayName || 'Not set'}</div>
                                </div>
                                <div>
                                  <div style={styles.detailLabel}>Email</div>
                                  <div style={styles.detailValue}>{u.email}</div>
                                </div>
                                <div>
                                  <div style={styles.detailLabel}>Role</div>
                                  <div style={styles.detailValue}>{isUserAdmin(u) ? 'Administrator' : 'User'}</div>
                                </div>
                                <div>
                                  <div style={styles.detailLabel}>Directory</div>
                                  <div style={styles.detailValue}>{u.dirName || 'Not set'}</div>
                                </div>
                                <div>
                                  <div style={styles.detailLabel}>Created</div>
                                  <div style={styles.detailValue}>{formatTimestamp(u.createdAt)}</div>
                                </div>
                                <div>
                                  <div style={styles.detailLabel}>Track Sessions</div>
                                  <div style={styles.detailValue}>{getUserSessionCount(u.id)}</div>
                                </div>
                              </div>
                              {/* App Activity */}
                              <div style={{ marginTop: 16 }}>
                                <div style={styles.detailLabel}>App Activity</div>
                                {(!u.appActivity || Object.keys(u.appActivity).length === 0) ? (
                                  <div style={{ ...styles.detailValue, color: 'var(--color-text-dim)' }}>No activity recorded</div>
                                ) : (
                                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
                                    <thead>
                                      <tr>
                                        <th style={{ ...styles.th, fontSize: 10, padding: '4px 8px' }}>App</th>
                                        <th style={{ ...styles.th, fontSize: 10, padding: '4px 8px' }}>Last Login</th>
                                        <th style={{ ...styles.th, fontSize: 10, padding: '4px 8px' }}>Logins</th>
                                        <th style={{ ...styles.th, fontSize: 10, padding: '4px 8px' }}>Uploads</th>
                                        <th style={{ ...styles.th, fontSize: 10, padding: '4px 8px' }}>Version</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Object.entries(u.appActivity).map(([app, act], i) => (
                                        <tr key={i}>
                                          <td style={{ ...styles.td, fontSize: 11, padding: '3px 8px' }}>{app}</td>
                                          <td style={{ ...styles.tdMono, fontSize: 11, padding: '3px 8px' }}>{formatTimestamp(act.lastLogin)}</td>
                                          <td style={{ ...styles.tdMono, fontSize: 11, padding: '3px 8px' }}>{act.logins || 0}</td>
                                          <td style={{ ...styles.tdMono, fontSize: 11, padding: '3px 8px' }}>{act.uploads || 0}</td>
                                          <td style={{ ...styles.tdMono, fontSize: 11, padding: '3px 8px' }}>{act.lastVersion || '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {/* Sessions Tab */}
            {activeTab === 'sessions' && sessionsLoading && (
              <LoadingProgress label="Loading sessions" expectedMs={12000} />
            )}
            {activeTab === 'sessions' && !sessionsLoading && (
              <table style={styles.table} data-doc-id="admin-sessions-table">
                <thead>
                  <tr>
                    <th style={styles.th}>Track</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Car</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>
                      <button
                        type="button"
                        style={styles.sortHeaderBtn}
                        onClick={() => setSessionSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
                        aria-label={`Sort sessions by date ${sessionSortOrder === 'desc' ? 'oldest first' : 'newest first'}`}
                        title={`Sort ${sessionSortOrder === 'desc' ? 'oldest first' : 'newest first'}`}
                      >
                        <span>Date</span>
                        <span>{sessionSortOrder === 'desc' ? 'Newest' : 'Oldest'}</span>
                      </button>
                    </th>
                    <th style={styles.th}>Laps</th>
                    <th style={styles.th}>Best Lap</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSessions.length === 0 ? (
                    <tr><td colSpan={7} style={styles.emptyRow}>No sessions found</td></tr>
                  ) : (
                    filteredSessions.map((s) => (
                      <tr key={s.id || s.sessionId} data-doc-id="admin-session-row">
                        <td
                          data-doc-id="admin-session-track-link"
                          style={{ ...styles.td, cursor: 'pointer', color: 'var(--color-accent)' }}
                          onClick={() => onSelectSession && onSelectSession({ ...s, isAdminView: true })}
                        >{s.track || '-'}</td>
                        <td style={styles.td}>
                          <SessionTypeIcon
                            sessionType={s.sessionType}
                            size={14}
                            style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-dim)' }}
                          />
                        </td>
                        <td style={styles.td}>{s.car || '-'}</td>
                        <td style={styles.tdMono}>{s.userEmail || '-'}</td>
                        <td style={styles.tdMono}>{formatSessionTimestamp(s)}</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>{s.lapCount || '-'}</td>
                        <td style={styles.tdMono}>{formatLapTime(s.bestLap)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {/* Tracks Tab */}
            {activeTab === 'tracks' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                  <div style={styles.syncStatus}>
                    <div>{githubTracks} default tracks | {customTracks} user-uploaded tracks | {tracks.filter(t => t.hasThumbnail).length} thumbnails</div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 11, color: 'var(--color-text-dim)' }}>
                      {Object.entries(trackTypeCounts).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                        <span key={type}>{count} {type}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      data-doc-id="admin-generate-all-thumbnails"
                      style={{ ...styles.syncBtn, opacity: thumbBusy ? 0.6 : 1 }}
                      onClick={handleGenerateMissingThumbnails}
                      disabled={thumbBusy}
                    >
                      Generate Missing
                    </button>
                    <button
                      style={{ ...styles.syncBtn, opacity: thumbBusy ? 0.6 : 1 }}
                      onClick={handleRegenerateAllThumbnails}
                      disabled={thumbBusy}
                    >
                      Regenerate All
                    </button>
                    <button
                      data-doc-id="admin-delete-all-thumbnails"
                      style={{ ...styles.deleteBtn, opacity: thumbBusy ? 0.6 : 1 }}
                      onClick={handleDeleteAllThumbnails}
                      disabled={thumbBusy}
                    >
                      Delete All Thumbnails
                    </button>
                    <button
                      style={{ ...styles.syncBtn, opacity: thumbBusy ? 0.6 : 1 }}
                      onClick={handleClearTrackCache}
                      disabled={thumbBusy}
                    >
                      Clear Cache
                    </button>
                    <button
                      data-doc-id="admin-toggle-debug"
                      style={{
                        padding: '6px 14px',
                        background: showDebug ? 'var(--color-primary)' : 'transparent',
                        border: '1px solid var(--color-border)',
                        borderRadius: 6,
                        color: showDebug ? '#fff' : 'var(--color-text-dim)',
                        fontFamily: 'var(--font-heading)',
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                      }}
                      onClick={() => setShowDebug(!showDebug)}
                    >
                      {showDebug ? 'Debug On' : 'Debug'}
                    </button>
                  </div>
                </div>
                {thumbStatus && (
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-text-dim)', marginBottom: 12 }}>
                    {thumbStatus}
                  </div>
                )}
                <table style={styles.table} data-doc-id="admin-tracks-table">
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, width: 60 }}>Preview</th>
                      <th style={styles.th}>Track Name</th>
                      <th style={styles.th}>Track ID</th>
                      <th style={styles.th}>Source</th>
                      {showDebug && <th style={styles.th}>GPS Points</th>}
                      <th style={styles.th}>Last Synced</th>
                      <th style={{ ...styles.th, width: 100 }}>Thumbnail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTracks.length === 0 ? (
                      <tr><td colSpan={showDebug ? 7 : 6} style={styles.emptyRow}>No tracks found. Add track CSV files to the racevoicetracks directory.</td></tr>
                    ) : (
                      filteredTracks.map((t) => (
                        <tr key={t.id || t.trackId} data-doc-id="admin-track-row">
                          <td style={{ ...styles.td, padding: '4px 8px' }}>
                            {t.hasThumbnail ? (
                              <TrackThumbnail
                                trackId={t.trackId}
                                alt={t.trackName}
                                style={{ width: 56, height: 40, borderRadius: 4, display: 'block' }}
                              />
                            ) : (
                              <div style={{ width: 56, height: 40, borderRadius: 4, background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>--</span>
                              </div>
                            )}
                          </td>
                          <td
                            data-doc-id="admin-track-link"
                            style={{ ...styles.td, cursor: 'pointer', color: 'var(--color-accent)' }}
                            onClick={() => onViewTrack && onViewTrack(t)}
                          >{t.trackName || '-'}</td>
                          <td style={styles.tdMono}>{t.trackId || '-'}</td>
                          <td style={styles.td}>
                            <span style={{
                              ...styles.badge,
                              ...(t.source === 'github' ? styles.badgeGithub : styles.badgeCustom),
                            }}>
                              {t.source === 'github' ? 'GitHub' : 'Custom'}
                            </span>
                            {t.source === 'user' && (
                              <span style={{ fontSize: 11, color: 'var(--color-text-dim)', marginLeft: 6 }}>
                                ({t.userId?.slice(0, 8)}...)
                              </span>
                            )}
                          </td>
                          {showDebug && <td style={{ ...styles.td, textAlign: 'center' }}>{t.pointCount || 0}</td>}
                          <td style={styles.tdMono}>{formatTimestamp(t.lastSynced)}</td>
                          <td style={styles.td}>
                            {t.hasThumbnail ? (
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button
                                  style={styles.actionBtn}
                                  onClick={() => handleGenerateOne(t.trackId)}
                                  disabled={thumbBusy}
                                  title="Regenerate thumbnail"
                                >
                                  Regen
                                </button>
                                <button
                                  style={styles.deleteBtn}
                                  onClick={() => handleDeleteOne(t.trackId)}
                                  disabled={thumbBusy}
                                >
                                  Del
                                </button>
                              </div>
                            ) : (
                              <button
                                style={styles.actionBtn}
                                onClick={() => handleGenerateOne(t.trackId)}
                                disabled={thumbBusy}
                              >
                                Generate
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </>
            )}

            {/* Cars Tab */}
            {activeTab === 'cars' && (
              <>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-text-dim)', marginBottom: 12 }}>
                  {cars.length} cars | {carMakeCount} makes{carsVersion ? ` | version ${carsVersion}` : ''}
                  {carSearch ? ` | ${filteredCars.length} shown` : ''}
                </div>
                <table style={styles.table} data-doc-id="admin-cars-table">
                  <thead>
                    <tr>
                      <th style={styles.th}>Make</th>
                      <th style={styles.th}>Model</th>
                      <th style={styles.th}>Year</th>
                      <th style={styles.th}>Horsepower</th>
                      <th style={styles.th}>Weight (lb)</th>
                      <th style={styles.th}>Max RPM</th>
                      <th style={styles.th}>Front Tire</th>
                      <th style={styles.th}>Rear Tire</th>
                      <th style={styles.th}>Gearbox</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCars.length === 0 ? (
                      <tr><td colSpan={9} style={styles.emptyRow}>No cars found.</td></tr>
                    ) : (
                      filteredCars.map((c, i) => (
                        <tr key={`${c.Make}|${c.Model}|${c.Year}|${i}`} data-doc-id="admin-car-row">
                          <td style={styles.td}>{c.Make || '-'}</td>
                          <td style={styles.td}>{c.Model || '-'}</td>
                          <td style={styles.tdMono}>{c.Year ?? '-'}</td>
                          <td style={styles.tdMono}>{c.Horsepower ?? '-'}</td>
                          <td style={styles.tdMono}>{c.Weight ?? '-'}</td>
                          <td style={styles.tdMono}>{c.maxRpm ?? '-'}</td>
                          <td style={styles.tdMono}>{c.tireSize?.front || '-'}</td>
                          <td style={styles.tdMono}>{c.tireSize?.rear || '-'}</td>
                          <td
                            style={styles.tdMono}
                            title={c.gearRatios ? `${(c.gearRatios.gears || []).join(', ')} | final ${c.gearRatios.final}` : undefined}
                          >
                            {c.gearRatios?.trans || '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </>
            )}

            {/* Activity Tab */}
            {activeTab === 'activity' && (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>User</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Last Login</th>
                    <th style={styles.th}>Time Ago</th>
                    <th style={styles.th}>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLogins.length === 0 ? (
                    <tr><td colSpan={5} style={styles.emptyRow}>No activity recorded</td></tr>
                  ) : (
                    recentLogins.map((u) => (
                      <tr key={u.id}>
                        <td style={styles.td}>{u.displayName || '-'}</td>
                        <td style={styles.tdMono}>{u.email}</td>
                        <td style={styles.tdMono}>{formatTimestamp(u.appActivity?.RaceVoiceCloud?.lastLogin)}</td>
                        <td style={styles.td}>{timeAgo(u.appActivity?.RaceVoiceCloud?.lastLogin)}</td>
                        <td style={styles.tdMono}>{formatTimestamp(u.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* Live Telemetry tab */}
        {activeTab === 'live' && (
          <LiveTelemetryPanel />
        )}

        {/* Lap Data Browser tab */}
        {activeTab === 'lapdata' && (
          <LapDataBrowser />
        )}

        {/* IP Address Map tab */}
        {activeTab === 'maps' && (
          <IpAddressMap users={users} />
        )}

        {/* Email / Broadcast tab */}
        {activeTab === 'email' && (
          <EmailPanel />
        )}
      </div>

      {/* Modals */}
      {showAddUser && (
        <AddUserModal
          onClose={() => setShowAddUser(false)}
          onUserCreated={handleUserCreated}
        />
      )}
      {passwordTarget && (
        <ChangePasswordModal
          targetUser={passwordTarget}
          onClose={() => setPasswordTarget(null)}
        />
      )}
    </div>
  );
}

export { ADMIN_EMAIL };
